import React from 'react';
import '../../styles/kyro-design.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="page">
      <div className="login-hero-min">
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner" style={{ textAlign: 'center', maxWidth: 'none' }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)' }}>Privacy Policy</h1>
          <p className="hero-sub" style={{ margin: '0.5rem 0 0', opacity: 0.7 }}>Last Updated: March 2, 2026</p>
        </div>
      </div>

      <div className="legal-content-min">
            <div>
              <h2>1. Introduction</h2>
              <p>
                Kyro Asset Management ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website or use our services.
              </p>
              <p>
                As a SEBI-registered Alternative Investment Fund (Category II) manager (SEBI Reg. No. IN/AIF2/26-27/2191), we adhere
                to strict confidentiality and data protection standards in accordance with applicable Indian 
                laws and regulations.
              </p>
            </div>

            <div>
              <h2>2. Information We Collect</h2>
              
              <h3>2.1 Personal Information</h3>
              <p>We may collect personal information that you voluntarily provide to us, including but not limited to:</p>
              <ul>
                <li>Name, email address, phone number, and contact details</li>
                <li>Professional information (company name, designation, industry)</li>
                <li>Financial information (investment experience, net worth, income details)</li>
                <li>Identity verification documents (PAN, Aadhaar, passport, etc.)</li>
                <li>Bank account details for transaction purposes</li>
                <li>Investment preferences and risk profile</li>
              </ul>

              <h3>2.2 Automatically Collected Information</h3>
              <p>When you visit our website, we automatically collect certain information:</p>
              <ul>
                <li>IP address, browser type, and operating system</li>
                <li>Pages visited, time spent, and navigation patterns</li>
                <li>Referring website and exit pages</li>
                <li>Device information and screen resolution</li>
              </ul>

              <h3>2.3 Cookies and Tracking Technologies</h3>
              <p>
                We use cookies and similar tracking technologies to enhance your experience, analyze 
                website traffic, and understand user behavior. You can control cookie preferences 
                through your browser settings.
              </p>
            </div>

            <div>
              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul>
                <li>Processing investment applications and KYC verification</li>
                <li>Providing investment management services</li>
                <li>Communicating about fund performance, updates, and opportunities</li>
                <li>Complying with legal and regulatory requirements (SEBI, PMLA, etc.)</li>
                <li>Conducting due diligence and risk assessment</li>
                <li>Improving our website and services</li>
                <li>Sending periodic newsletters and marketing communications (with consent)</li>
                <li>Preventing fraud and ensuring security</li>
                <li>Responding to inquiries and providing customer support</li>
              </ul>
            </div>

            <div>
              <h2>4. Information Sharing and Disclosure</h2>
              
              <h3>4.1 Service Providers</h3>
              <p>
                We may share your information with trusted third-party service providers who assist us 
                in operating our business, including:
              </p>
              <ul>
                <li>Custodians and depositories</li>
                <li>Fund administrators and auditors</li>
                <li>Legal and compliance advisors</li>
                <li>Technology and hosting providers</li>
                <li>KYC and identity verification agencies</li>
              </ul>

              <h3>4.2 Regulatory Authorities</h3>
              <p>
                We may disclose your information to regulatory authorities including SEBI, Income Tax 
                Department, Financial Intelligence Unit (FIU), and other government agencies as required 
                by law.
              </p>

              <h3>4.3 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be 
                transferred to the acquiring entity, subject to the same privacy protections.
              </p>

              <h3>4.4 Legal Requirements</h3>
              <p>We may disclose your information when required to:</p>
              <ul>
                <li>Comply with legal obligations or court orders</li>
                <li>Protect our rights, property, or safety</li>
                <li>Prevent fraud or illegal activities</li>
                <li>Enforce our terms and conditions</li>
              </ul>
            </div>

            <div>
              <h2>5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These 
                measures include:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and employee training</li>
                <li>Secure backup and disaster recovery procedures</li>
              </ul>
              <p>
                However, no method of transmission over the internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2>6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes 
                outlined in this Privacy Policy, unless a longer retention period is required or permitted 
                by law. Investment-related records are retained in accordance with SEBI regulations and 
                applicable tax laws, typically for a minimum of 7-10 years.
              </p>
            </div>

            <div>
              <h2>7. Your Rights</h2>
              <p>Subject to applicable laws, you have the following rights:</p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your information (subject to legal obligations)</li>
                <li><strong>Objection:</strong> Object to processing of your information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong>Withdrawal of Consent:</strong> Withdraw consent for marketing communications</li>
              </ul>
              <p>
                To exercise these rights, please contact us at <a href="mailto:compliance@kyrocapital.in">compliance@kyrocapital.in</a>
              </p>
            </div>

            <div>
              <h2>8. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the 
                privacy practices of these external sites. We encourage you to review their privacy 
                policies before providing any personal information.
              </p>
            </div>

            <div>
              <h2>9. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 18. We do not knowingly 
                collect personal information from minors. If we become aware that we have collected 
                information from a minor, we will take steps to delete it promptly.
              </p>
            </div>

            <div>
              <h2>10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than India. 
                We ensure that appropriate safeguards are in place to protect your information in 
                accordance with this Privacy Policy and applicable data protection laws.
              </p>
            </div>

            <div>
              <h2>11. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices 
                or legal requirements. We will notify you of any material changes by posting the updated 
                policy on our website with a revised "Last Updated" date. Your continued use of our 
                services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2>12. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our 
                data practices, please contact us:
              </p>
              <div style={{ background: 'var(--off)', padding: '1rem', border: '1px solid var(--gray-lt)', marginTop: '1rem' }}>
                <p><strong>Kyro Asset Management</strong></p>
                <p>Email: <a href="mailto:compliance@kyrocapital.in">compliance@kyrocapital.in</a></p>
                <p>Phone: +91 76 111 111 02</p>
                <p>Address: 303, Unity One, Rajiv Gandhi Square, Indore – 452001</p>
              </div>
            </div>

            <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--gray-lt)' }}>
              <p>
                <strong>Note:</strong> This Privacy Policy is subject to the laws of India. Any disputes 
                arising from this policy shall be subject to the exclusive jurisdiction of courts in Indore, India.
              </p>
            </div>
          </div>
    </div>
  );
};

export default PrivacyPolicy;
